import { Heading, HeadingLevel, useDialogStore } from "@ariakit/react";
import { cx } from "@indietabletop/appkit";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { cloneElement, type ReactElement, type ReactNode } from "react";
import { PageHeaderNavButton } from "../../PageHeaderButton/PageHeaderItem";
import { SidenavContext } from "./SidenavContext";
import { header, layout, page, section, spine, split, view } from "./style.css";

export function PageHeaderItem(props: {
  render?: ReactElement;
  children?: ReactNode;
  className?: string;
}) {
  return cloneElement(
    props.render ?? <div />,
    cx(props, header.item),
    props.children,
  );
}

export function PageHeaderTitle(props: { children: ReactNode }) {
  const { children } = props;
  return <div className={header.title}>{children}</div>;
}

export function PageHeader(props: {
  children: [navActions: ReactNode, title: ReactNode, action?: ReactNode];
}) {
  const [navAction, title, action] = props.children;

  return (
    <div className={header.container}>
      {navAction}
      {title}
      {action}
    </div>
  );
}

export function PageContent(props: { children: ReactNode }) {
  const { children } = props;

  return (
    <HeadingLevel>
      <div className={page.content}>{children}</div>
    </HeadingLevel>
  );
}

export function PageContainer(props: {
  children: ReactNode;
  className?: string;
}) {
  const { children } = props;

  return <div {...cx(props, page.container)}>{children}</div>;
}

/**
 * Full-height page with a header and scrollable content.
 *
 * Pass a theme class (see `theme` in `src/theme.css.ts`) via `className` to
 * re-theme the page and the shop components rendered within it.
 */
export function Page(props: {
  title?: ReactNode;
  actions?: ReactNode;
  backHref: string;
  children: ReactNode;
  className?: string;
}) {
  const { title, actions, backHref, children, className } = props;

  return (
    <HeadingLevel>
      <PageContainer className={className}>
        <PageHeader>
          <PageHeaderNavButton backHref={backHref} />

          <PageHeaderTitle>{title}</PageHeaderTitle>
          {actions}
        </PageHeader>

        <PageContent>{children}</PageContent>
      </PageContainer>
    </HeadingLevel>
  );
}

export function SpineItem(props: {
  render?: ReactElement;
  children?: ReactNode;
  className?: string;
}) {
  return cloneElement(
    props.render ?? <div />,
    cx(props, spine.item),
    props.children,
  );
}

export function SpineCenter(props: { children: ReactNode }) {
  const { children } = props;
  return <div className={spine.center}>{children}</div>;
}

export function SpineContainer(props: {
  children: [start: ReactElement, center: ReactElement, end: ReactElement];
}) {
  return <div className={spine.container}>{props.children}</div>;
}

export function SplitView(props: {
  children: [start: ReactElement, end: ReactElement];
}) {
  const [start, end] = props.children;

  return (
    <div className={split.container}>
      {start}
      <div className={split.divider} />
      {end}
    </div>
  );
}

export function ViewContent(
  props: { className?: string; children?: ReactNode } & RecipeVariants<
    typeof view.container
  >,
) {
  return (
    <div
      {...cx(
        props,
        view.container({
          blockVariant: props.blockVariant,
          inlineVariant: props.inlineVariant,
          isCentered: props.isCentered,
        }),
      )}
    >
      {props.children}
    </div>
  );
}

export function SectionContainer(props: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <HeadingLevel>
      <div {...cx(props, section.container)}>{props.children}</div>
    </HeadingLevel>
  );
}

export function SectionContent(props: {
  className?: string;
  children?: ReactNode;
}) {
  return <div {...cx(props, section.content)}>{props.children}</div>;
}

export function SectionHeading(props: {
  className?: string;
  children?: ReactNode;
}) {
  return <Heading {...cx(props, section.heading)}>{props.children}</Heading>;
}

export function SectionParagraph(props: {
  children: ReactNode;
  className?: string;
}) {
  return <p {...cx(props, section.paragraph)}>{props.children}</p>;
}

/**
 * Layout component used in most of the app.
 */
export function MainLayout(props: {
  children: ReactNode;
  spine: ReactElement;
}) {
  const { children, spine } = props;
  const store = useDialogStore();

  return (
    <SidenavContext value={store}>
      {spine}

      <div className={layout.container}>{children}</div>
    </SidenavContext>
  );
}
